import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DepartmentMetricsTable = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedMetric, setSelectedMetric] = useState('hires');

  const departmentData = [
    {
      department: 'Engineering',
      openPositions: 12,
      applications: 847,
      interviews: 156,
      hires: 28,
      avgTimeToHire: 22,
      costPerHire: 4200,
      offerAcceptance: 89,
      hiringManager: 'Sarah Johnson',
      trend: 'up',
      trendValue: '+32%',
      targetHires: 35,
      budgetUtilized: 78
    },
    {
      department: 'Product',
      openPositions: 8,
      applications: 523,
      interviews: 98,
      hires: 15,
      avgTimeToHire: 28,
      costPerHire: 3800,
      offerAcceptance: 85,
      hiringManager: 'Mike Chen',
      trend: 'up',
      trendValue: '+18%',
      targetHires: 20,
      budgetUtilized: 65
    },
    {
      department: 'Marketing',
      openPositions: 6,
      applications: 392,
      interviews: 72,
      hires: 11,
      avgTimeToHire: 25,
      costPerHire: 2900,
      offerAcceptance: 91,
      hiringManager: 'Emily Davis',
      trend: 'stable',
      trendValue: '+5%',
      targetHires: 15,
      budgetUtilized: 72
    },
    {
      department: 'Sales',
      openPositions: 15,
      applications: 634,
      interviews: 124,
      hires: 22,
      avgTimeToHire: 19,
      costPerHire: 2400,
      offerAcceptance: 88,
      hiringManager: 'Alex Rodriguez',
      trend: 'up',
      trendValue: '+28%',
      targetHires: 25,
      budgetUtilized: 88
    },
    {
      department: 'Design',
      openPositions: 4,
      applications: 267,
      interviews: 45,
      hires: 7,
      avgTimeToHire: 31,
      costPerHire: 3600,
      offerAcceptance: 82,
      hiringManager: 'Lisa Wang',
      trend: 'down',
      trendValue: '-8%',
      targetHires: 10,
      budgetUtilized: 58
    },
    {
      department: 'Operations',
      openPositions: 9,
      applications: 445,
      interviews: 89,
      hires: 16,
      avgTimeToHire: 24,
      costPerHire: 2200,
      offerAcceptance: 87,
      hiringManager: 'David Kim',
      trend: 'up',
      trendValue: '+15%',
      targetHires: 18,
      budgetUtilized: 81
    }
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    let sortableData = [...departmentData];
    if (sortConfig !== null) {
      sortableData?.sort((a, b) => {
        if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [sortConfig]);

  const getSortIcon = (columnName) => {
    if (sortConfig?.key === columnName) {
      return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
    }
    return 'ChevronsUpDown';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return 'TrendingUp';
      case 'down': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-emerald-600';
      case 'down': return 'text-red-600';
      default: return 'text-slate-500';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const metrics = [
    { key: 'hires', label: 'Hires', icon: 'Users' },
    { key: 'applications', label: 'Applications', icon: 'FileText' },
    { key: 'interviews', label: 'Interviews', icon: 'Calendar' },
    { key: 'avgTimeToHire', label: 'Avg. Time', icon: 'Clock' },
    { key: 'costPerHire', label: 'Cost/Hire', icon: 'DollarSign' },
    { key: 'offerAcceptance', label: 'Acceptance', icon: 'CheckCircle' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Department Performance</h3>
          <p className="text-sm text-slate-500">Hiring metrics and efficiency by department</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
            <Icon name="BarChart3" size={16} className="text-slate-600" />
            <span className="text-sm text-slate-600">Focus:</span>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e?.target?.value)}
              className="bg-transparent text-sm font-medium text-slate-900 border-none outline-none"
            >
              {metrics?.map(metric => (
                <option key={metric?.key} value={metric?.key}>{metric?.label}</option>
              ))}
            </select>
          </div>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-3">
            <Icon name="Building2" size={20} className="text-blue-600" />
            <div>
              <div className="text-2xl font-bold text-blue-900">{departmentData?.length}</div>
              <div className="text-sm text-blue-700">Active Departments</div>
            </div>
          </div>
        </div>
        <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-3">
            <Icon name="Target" size={20} className="text-emerald-600" />
            <div>
              <div className="text-2xl font-bold text-emerald-900">
                {departmentData?.reduce((sum, dept) => sum + dept?.hires, 0)}
              </div>
              <div className="text-sm text-emerald-700">Total Hires</div>
            </div>
          </div>
        </div>
        <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg border border-purple-200">
          <div className="flex items-center gap-3">
            <Icon name="Briefcase" size={20} className="text-purple-600" />
            <div>
              <div className="text-2xl font-bold text-purple-900">
                {departmentData?.reduce((sum, dept) => sum + dept?.openPositions, 0)}
              </div>
              <div className="text-sm text-purple-700">Open Positions</div>
            </div>
          </div>
        </div>
        <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-3">
            <Icon name="TrendingUp" size={20} className="text-amber-600" />
            <div>
              <div className="text-2xl font-bold text-amber-900">
                {(departmentData?.reduce((sum, dept) => sum + (dept?.hires / dept?.targetHires * 100), 0) / departmentData?.length)?.toFixed(0)}%
              </div>
              <div className="text-sm text-amber-700">Avg. Target Achievement</div>
            </div>
          </div>
        </div>
      </div>
      {/* Department Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-4 text-sm font-semibold text-slate-700">Department</th>
                <th 
                  className="text-left p-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('openPositions')}
                >
                  <div className="flex items-center gap-1">
                    Open Roles
                    <Icon name={getSortIcon('openPositions')} size={14} className="text-slate-400" />
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('hires')}
                >
                  <div className="flex items-center gap-1">
                    Hires
                    <Icon name={getSortIcon('hires')} size={14} className="text-slate-400" />
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('avgTimeToHire')}
                >
                  <div className="flex items-center gap-1">
                    Avg. Time
                    <Icon name={getSortIcon('avgTimeToHire')} size={14} className="text-slate-400" />
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('costPerHire')}
                >
                  <div className="flex items-center gap-1">
                    Cost/Hire
                    <Icon name={getSortIcon('costPerHire')} size={14} className="text-slate-400" />
                  </div>
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700">Target Progress</th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700">Performance</th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700">Manager</th>
              </tr>
            </thead>
            <tbody>
              {sortedData?.map((dept, index) => {
                const targetProgress = (dept?.hires / dept?.targetHires * 100);
                const isHighPerformer = targetProgress >= 80;
                
                return (
                  <tr key={dept?.department} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm ${
                          isHighPerformer ? 'bg-emerald-500' : 'bg-slate-400'
                        }`}>
                          {dept?.department?.substring(0, 2)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{dept?.department}</div>
                          <div className="text-sm text-slate-500">{dept?.applications} applications</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-slate-900 font-medium">{dept?.openPositions}</div>
                      <div className="text-sm text-slate-500">{dept?.interviews} interviews</div>
                    </td>
                    <td className="p-4">
                      <div className="text-slate-900 font-medium">{dept?.hires}</div>
                      <div className="text-sm text-slate-500">{dept?.offerAcceptance}% acceptance</div>
                    </td>
                    <td className="p-4">
                      <div className="text-slate-900 font-medium">{dept?.avgTimeToHire} days</div>
                      <div className="text-xs text-slate-500">Industry: 28d</div>
                    </td>
                    <td className="p-4">
                      <div className="text-slate-900 font-medium">${dept?.costPerHire?.toLocaleString()}</div>
                      <div className="text-xs text-slate-500">Budget: {dept?.budgetUtilized}%</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-900">{targetProgress?.toFixed(0)}%</span>
                        <span className="text-xs text-slate-500">({dept?.hires}/{dept?.targetHires})</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-500 ${getProgressColor(targetProgress)}`}
                          style={{ width: `${Math.min(targetProgress, 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className="p-4">
                      <div className={`flex items-center gap-1 ${getTrendColor(dept?.trend)}`}>
                        <Icon name={getTrendIcon(dept?.trend)} size={16} />
                        <span className="text-sm font-medium">{dept?.trendValue}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">vs last quarter</div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-slate-900">{dept?.hiringManager}</div>
                      <div className="text-xs text-slate-500">Hiring Manager</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Performance Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="TrendingUp" size={18} className="text-emerald-600" />
            <h4 className="font-semibold text-emerald-900">Top Performer</h4>
          </div>
          <p className="text-sm text-emerald-800">
            <strong>Sales Department</strong> leads with 88% target achievement and lowest cost per hire at $2,400.
            Their 19-day average time-to-hire is 32% faster than company average.
          </p>
        </div>
        
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertTriangle" size={18} className="text-amber-600" />
            <h4 className="font-semibold text-amber-900">Needs Attention</h4>
          </div>
          <p className="text-sm text-amber-800">
            <strong>Design Department</strong> is 30% below target with 31-day hiring cycle.
            Consider process optimization and additional sourcing channels.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DepartmentMetricsTable;